import { useMediaQuery } from '@/hooks/use-media-query';
import { ReactNode, useEffect, useState } from 'react';
import { NavigationHeader } from './NavigationHeader';
import NavigationSidebar from './NavigationSidebar';
import { SidebarProvider, useSidebar } from '@/contexts/SidebarContext';

interface AppLayoutProps {
  children: ReactNode;
}

function AppLayoutContent({ children }: AppLayoutProps) {
  const { isCollapsed: isSidebarCollapsed } = useSidebar();
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  // Detect if we're on mobile (below 768px) - tablet and desktop use fixed sidebar
  const isMobile = useMediaQuery('(max-width: 767px)');

  // Function to handle mobile sidebar toggle
  const handleMobileSidebarToggle = () => {
    setIsMobileSidebarOpen((prev) => !prev);
  };

  // Close mobile sidebar when switching to tablet/desktop view
  useEffect(() => {
    if (!isMobile) {
      setIsMobileSidebarOpen(false);
    }
  }, [isMobile]);

  return (
    <div className="bg-slate-50 flex h-screen min-h-0 supports-[height:100dvh]:h-dvh">
      {/* Sidebar Navigation */}
      <NavigationSidebar
        isMobileOpen={isMobileSidebarOpen}
        onMobileClose={() => setIsMobileSidebarOpen(false)}
        isMobileOrTablet={isMobile}
      />

      {/* Main Content Area */}
      <div
        className={`flex min-w-0 min-h-0 flex-1 flex-col transition-all duration-300 ${isMobile ? 'ml-0' : isSidebarCollapsed ? 'ml-16' : 'ml-64'
          }`}
      >
        {/* Top Header */}
        <NavigationHeader onMobileSidebarToggle={handleMobileSidebarToggle} />

        {/* Main Content */}
        <main className="min-h-0 flex-1 w-full overflow-y-auto">{children}</main>
      </div>
    </div>
  );
}

export default function AppLayout({ children }: AppLayoutProps) {
  return (
    <SidebarProvider>
      <AppLayoutContent>{children}</AppLayoutContent>
    </SidebarProvider>
  );
}
